import { omaSATOIdentityUtils } from '@sato/components';
import { makeAutoObservable } from 'mobx';
import { create, persist } from 'mobx-persist';

import { authApi } from '@api/auth.api';
import { GA_EVENT_TYPES } from '@models/analyticsEvents.types';
import { queryClient } from '@providers/QueryProvider';
import { sendGoogleAnalyticsEvent } from '@utils/analytics.utils';
import { isLocalEnv } from '@utils/featureFlags';
import { clearGiosg } from '@utils/giosg.utils';

class AuthStore {
  /** Persisted email used in login */
  @persist rememberedEmail: string | null = null;

  /** Credentials of a user who tried to log in before finishing their registration. Not persisted. */
  unfinishedRegistrationCredentials: {
    email: string;
    password: string;
  } | null = null;

  accessToken = omaSATOIdentityUtils.getAccessToken();
  idToken = omaSATOIdentityUtils.getIdToken();
  refreshToken = omaSATOIdentityUtils.getRefreshToken();

  /**
   * - `logged-out` - Deliberately logged-out
   * - `unauthenticated` - Auth credentials not having been validated, for example, on application start.
   * - `initial-token-refresh` - Token refresh is in-flight on application start.
   * - `authenticated` - Logged in: User has valid session and credentials
   * - `logging-out` - In-between state between `authenticated` and `logged-out`, where we are logging out but still have authentication credentials
   */
  status:
    | 'unauthenticated'
    | 'initial-token-refresh'
    | 'authenticated'
    | 'logged-out'
    | 'logging-out' = 'unauthenticated';

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    /* refresh session tokens on application start if possible so that
     * `idToken` contract data is up-to-date or a previously authenticated
     * user is automatically signed in even if auth token and ID token have expired
     * but the refresh token is still valid */
    if (this.canAuthenticate) {
      this.setStatus('initial-token-refresh');

      this.refresh({ throwErrors: false }).then(() => {
        if (isLocalEnv) console.info('[AuthStore] inited tokens');
      });
    }
  }

  get isAuthenticated() {
    return !!this.accessToken && !!this.idToken;
  }

  get canAuthenticate() {
    return !!this.refreshToken;
  }

  setStatus(status: typeof this.status) {
    this.status = status;
  }

  setRememberedEmail(email: string | null) {
    this.rememberedEmail = email;
  }

  setUnfinishedRegistrationCredentials(
    credentials: typeof this.unfinishedRegistrationCredentials
  ) {
    this.unfinishedRegistrationCredentials = credentials;
  }

  async refresh({
    throwErrors = true,
  }: Partial<{ throwErrors: boolean }> = {}) {
    try {
      const { idToken, token, expiresIn } = await authApi.refreshTokens();

      // update cookies
      omaSATOIdentityUtils.setLoginTokens({
        idToken,
        accessToken: token,
        ...(expiresIn && {
          options: {
            accessTokenExpiration: expiresIn / 60,
            idTokenExpiration: expiresIn / 60,
          },
        }),
      });

      // sync cookies with auth store state
      this.syncTokens();

      this.setStatus('authenticated');
    } catch (err) {
      // do not redirect on auto-refresh
      await this.logout();

      // logout if initializing credentials failed
      if (throwErrors) throw err;
    }
  }

  async login(email: string, password: string) {
    return authApi.signIn(email, password).then(({ data }) => {
      const { idToken, refreshToken, token, expiresIn } = data;

      omaSATOIdentityUtils.setLoginTokens({
        idToken,
        accessToken: token,
        refreshToken,
        ...(expiresIn && {
          options: {
            accessTokenExpiration: expiresIn / 60,
            idTokenExpiration: expiresIn / 60,
          },
        }),
      });

      this.syncTokens();
      this.setStatus('authenticated');
    });
  }

  /** Reset auth store state from cookies */
  syncTokens() {
    this.accessToken = omaSATOIdentityUtils.getAccessToken();
    this.idToken = omaSATOIdentityUtils.getIdToken();
    this.refreshToken = omaSATOIdentityUtils.getRefreshToken();
  }

  /** Clear cookies and reset state */
  reset() {
    omaSATOIdentityUtils.clearCookies();

    this.accessToken = undefined;
    this.idToken = undefined;
    this.refreshToken = undefined;
  }

  /** Clear auth state, stores and related dependencies and navigate to login */
  async logout() {
    // early-exit if log-out was already invoked
    if (this.status === 'logged-out' || this.status === 'logging-out') return;

    this.setStatus('logging-out');

    if (isLocalEnv) console.info('Logging out...');

    // sign-out only if there are credentials, i.e. a session from which to log-out
    if (this.isAuthenticated) await authApi.signOut();

    // reset state and cookies immediately after calling backend sign-out to avoid render cycles
    this.reset();

    clearGiosg();
    sendGoogleAnalyticsEvent({ event: GA_EVENT_TYPES.LOGOUT });

    queryClient.clear();

    this.setStatus('logged-out');
  }
}

export const authStore = new AuthStore();

create({})('omasato-auth', authStore).then((state) => {
  if (isLocalEnv) console.info('[AuthStore] inited:', state);
});
